import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useVideo } from '../context/VideoContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const { FiStar, FiMapPin, FiBriefcase, FiDollarSign, FiSearch, FiCheck, FiX, FiLinkedin, FiCheckCircle } = FiIcons;
const { FaStar, FaStarHalfAlt, FaRegStar } = FaIcons;

const parseWhyItWorks = (text) => {
  if (!text) return [];
  return text
    .split('\n')
    .map(line => line.replace(/^[-–•]\s*/, '').trim())
    .filter(line => line.length > 0);
};

const renderWithLinks = (text) => {
  const parts = [];
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let lastIndex = 0;
  let match;
  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-orange-400 underline underline-offset-2 hover:text-orange-300 transition-colors"
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
};

const Directory = () => {
  const { companies, loading } = useVideo();
  const [search, setSearch] = useState(new URLSearchParams(window.location.search).get('name') || '');

  const filtered = companies
    .filter(c => {
      const term = search.toLowerCase();
      if (!term) return true;
      const inName = c.name?.toLowerCase().includes(term) ?? false;
      const inPros = c.pros?.some(p => p?.toLowerCase().includes(term)) ?? false;
      const inCons = c.cons?.some(con => con?.toLowerCase().includes(term)) ?? false;
      const inFounders = c.notable_people?.some(p => p?.name?.toLowerCase().includes(term)) ?? false;
      return inName || inPros || inCons || inFounders;
    })
    .sort((a, b) => Number(b.overall_rating) - Number(a.overall_rating));

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<SafeIcon key={i} icon={FaStar} className="text-amber-500" />);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<SafeIcon key={i} icon={FaStarHalfAlt} className="text-amber-500" />);
      } else {
        stars.push(<SafeIcon key={i} icon={FaRegStar} className="text-slate-700" />);
      }
    }
    return stars;
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
      <div className="w-12 h-12 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen pb-20 pt-32 px-6">
      <Helmet>
        <title>Production Discovery | Top Video Agencies & Production Houses</title>
        <meta name="description" content="Discover and compare the world's most elite video production agencies. Verified ratings, portfolio sizes, and pricing for your next explainer video." />
        <meta property="og:title" content="Production Discovery | Directory of Elite Video Agencies" />
        <meta property="og:description" content="Find the perfect production partner for your marketing videos. Compare top agencies based on real performance data." />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        <header className="mb-16 text-center">
          <motion.h1
            className="text-5xl md:text-7xl font-black text-white mb-6 uppercase tracking-tighter"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Production <span className="text-orange-500">Discovery</span>
          </motion.h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Discover the world's most elite video production agencies, verified for quality and performance.
          </p>

          <div className="mt-12 max-w-xl mx-auto relative">
            <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 -translate-y-1/2 text-white/30 text-xl" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-16 pr-8 py-5 bg-white/5 border border-white/10 rounded-full text-white text-lg focus:border-orange-500 outline-none transition-all shadow-2xl"
            />
          </div>

          <div className="mt-4">
            <a
              href="https://whatastory.link/shortlist"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 text-sm hover:text-slate-400 transition-colors underline underline-offset-2"
            >
              Help me shortlist, no strings attached
            </a>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filtered.map((company, idx) => (
            <CompanyCard
              key={company.id}
              company={company}
              idx={idx}
              renderRatingStars={renderRatingStars}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const CompanyCard = ({ company, idx, renderRatingStars }) => {
  const [hovered, setHovered] = useState(false);
  const bullets = parseWhyItWorks(company.why_it_works);
  const hasOverlay = bullets.length > 0;

  return (
    <motion.div
      className="glass-card p-10 border-white/5 group relative overflow-hidden"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: idx * 0.1 }}
      onMouseEnter={() => hasOverlay && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Rank Badge */}
      <div className="absolute top-0 left-0 px-6 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-black text-xl rounded-br-3xl shadow-lg z-10">
        #{idx + 1}
      </div>

      {/* Key Points Hover Overlay */}
      <AnimatePresence>
        {hovered && hasOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-20 flex flex-col justify-center px-10 py-12 rounded-[inherit]"
            style={{
              backdropFilter: 'blur(5px)',
              backgroundColor: 'rgba(9, 9, 11, 0.80)',
            }}
          >
            <h4 className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em] mb-5">
              Key Points
            </h4>
            <ul className="space-y-4 overflow-y-auto max-h-[340px] pr-1">
              {bullets.map((line, i) => (
                <motion.li
                  key={i}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <SafeIcon icon={FiCheckCircle} className="text-orange-500 mt-1 flex-shrink-0" />
                  <span className="text-sm text-slate-300 leading-relaxed font-medium">
                    {renderWithLinks(line)}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col md:flex-row gap-10">
        <div className="w-32 h-32 rounded-3xl bg-zinc-900 border border-white/5 p-4 flex-shrink-0 flex items-center justify-center overflow-hidden self-start mt-6">
          {company.logo_url ? (
            <img src={company.logo_url} className="w-full h-full object-contain" alt={company.name} />
          ) : (
            <span className="text-white/20 font-black text-3xl">{company.name[0]}</span>
          )}
        </div>

        <div className="flex-1">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-3xl font-black text-white group-hover:text-orange-400 transition-colors uppercase tracking-tight">{company.name}</h2>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-3 py-1 bg-orange-500/10 text-orange-500 text-[10px] font-black uppercase rounded-full border border-orange-500/20">{company.speciality}</span>
                <span className="text-slate-500 text-xs font-bold">• Active since {company.active_since}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-white">{Number(company.overall_rating).toFixed(1)}</div>
              <div className="flex gap-0.5 text-xs justify-end mt-1">
                {renderRatingStars(company.overall_rating)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-8 py-6 border-y border-white/5">
            <StatItem
              label="Clutch"
              value={
                <span>
                  {Number(company.clutch_rating).toFixed(1)}/5.0
                  {company.clutch_review_count && company.clutch_review_count !== '0' && (
                    <span className="text-slate-500 text-xs ml-1 font-normal">({company.clutch_review_count})</span>
                  )}
                </span>
              }
              icon={FiStar}
            />
            <StatItem label="SaaS Projects" value={company.portfolio_size} icon={FiBriefcase} />
            <StatItem label="Starting" value={company.avg_price} icon={FiDollarSign} />
          </div>

          <div className="space-y-8">
            <div>
              <h4 className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] mb-4">Notable Team</h4>
              <div className="flex flex-wrap gap-6">
                {company.notable_people?.map((person, i) => (
                  <div key={i} className="flex items-center gap-3 group/person">
                    <div className="relative">
                      <img
                        src={person.photo_url || `https://ui-avatars.com/api/?name=${person.name}&background=random`}
                        className="w-12 h-12 rounded-xl object-cover border-2 border-white/10 group-hover/person:border-orange-500 transition-all"
                        alt={person.name}
                      />
                      {person.linkedin && (
                        <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="absolute -bottom-1 -right-1 p-1 bg-blue-600 rounded-md text-white text-[8px] opacity-0 group-hover/person:opacity-100 transition-opacity">
                          <SafeIcon icon={FiLinkedin} />
                        </a>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white text-xs font-bold truncate">{person.name}</div>
                      <div className="text-slate-500 text-[10px] uppercase font-black tracking-wider truncate">{person.position || 'Executive'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div>
                <h4 className="text-green-500 text-[10px] font-black uppercase tracking-[0.2em] mb-3">The Pros</h4>
                <ul className="space-y-2">
                  {company.pros?.map((pro, i) => (
                    <li key={i} className="text-xs text-slate-400 flex items-start gap-2">
                      <SafeIcon icon={FiCheck} className="text-green-500 shrink-0 mt-0.5" /> {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-red-500 text-[10px] font-black uppercase tracking-[0.2em] mb-3">The Cons</h4>
                <ul className="space-y-2">
                  {company.cons?.map((con, i) => (
                    <li key={i} className="text-xs text-slate-400 flex items-start gap-2">
                      <SafeIcon icon={FiX} className="text-red-500 shrink-0 mt-0.5" /> {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const StatItem = ({ label, value, icon: Icon }) => (
  <div>
    <div className="flex items-center gap-1.5 text-white/30 text-[9px] font-black uppercase tracking-widest mb-1">
      <SafeIcon icon={Icon} className="text-orange-500/50" /> {label}
    </div>
    <div className="text-white font-bold">{value}</div>
  </div>
);

export default Directory;